/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
export const DEFAULT_ENDPOINT = '/api/pricing'

// ---------------------------------------------------------------------------
// Built-in upstream ratio presets
//
// The backend (`controller/ratio_sync.go`) synthesizes two virtual channels and
// returns them in the syncable channels response. The constants below mirror
// the backend literals one-to-one; do NOT translate the *_NAME values because
// they are wire-protocol identifiers, not user-facing labels.
//
// Identification on the frontend should rely on the stable negative ID alone.
// `*_NAME` and `*_BASE_URL` are kept for diagnostics, custom channel
// detection, and backwards compatibility with the classic frontend.
// ---------------------------------------------------------------------------

export const OFFICIAL_CHANNEL_ID = -100
export const OFFICIAL_CHANNEL_NAME = '官方倍率预设'
export const OFFICIAL_CHANNEL_BASE_URL = 'https://basellm.github.io'
export const OFFICIAL_CHANNEL_ENDPOINT =
  '/llm-metadata/api/newapi/ratio_config-v1-base.json'

export const MODELS_DEV_PRESET_ID = -101
export const MODELS_DEV_PRESET_NAME = 'models.dev 价格预设'
export const MODELS_DEV_PRESET_BASE_URL = 'https://models.dev'
export const MODELS_DEV_PRESET_ENDPOINT = 'https://models.dev/api.json'

// ---------------------------------------------------------------------------
// Vendor official price sources (厂商官网价格源)
//
// The backend fetches the vendor's official pricing page, parses it and
// converts CNY per 1M tokens into local ratios. Selecting one of these
// presets and confirming is all it takes to sync — no manual entry.
// Mirrors `controller/vendor_pricing_source.go`.
// ---------------------------------------------------------------------------

export const TENCENT_VENDOR_PRESET_ID = -102
export const TENCENT_VENDOR_PRESET_NAME = '腾讯云官网价格'
export const TENCENT_VENDOR_BASE_URL = 'https://cloud.tencent.com'
export const TENCENT_VENDOR_ENDPOINT = 'vendor:tencent'

export const HUAWEI_VENDOR_PRESET_ID = -103
export const HUAWEI_VENDOR_PRESET_NAME = '华为云官网价格'
export const HUAWEI_VENDOR_BASE_URL = 'https://support.huaweicloud.com'
export const HUAWEI_VENDOR_ENDPOINT = 'vendor:huawei'

// 厂商价格源预设 ID -> 该渠道在界面上应显示的端点标识。
// 后端只按 ID 分派（不请求这个端点），它纯粹用于在 UI 里表明这是官网价格源。
export const VENDOR_PRESET_ENDPOINTS: Record<number, string> = {
  [TENCENT_VENDOR_PRESET_ID]: TENCENT_VENDOR_ENDPOINT,
  [HUAWEI_VENDOR_PRESET_ID]: HUAWEI_VENDOR_ENDPOINT,
}

export type VendorPreset = {
  id: number
  /** 下拉里显示给站长的短名（渠道类型） */
  label: string
  /** 与后端 `vendorPriceSources[].Name` 完全一致，抓取时回传 */
  name: string
  baseUrl: string
  endpoint: string
}

// 一键对齐官网价对话框里可选的厂商。顺序 = 下拉顺序，第一个为默认选中项。
export const VENDOR_PRESETS: VendorPreset[] = [
  {
    id: TENCENT_VENDOR_PRESET_ID,
    label: '腾讯云',
    name: TENCENT_VENDOR_PRESET_NAME,
    baseUrl: TENCENT_VENDOR_BASE_URL,
    endpoint: TENCENT_VENDOR_ENDPOINT,
  },
  {
    id: HUAWEI_VENDOR_PRESET_ID,
    label: '华为云',
    name: HUAWEI_VENDOR_PRESET_NAME,
    baseUrl: HUAWEI_VENDOR_BASE_URL,
    endpoint: HUAWEI_VENDOR_ENDPOINT,
  },
]

export function getVendorPreset(id: number): VendorPreset | undefined {
  return VENDOR_PRESETS.find((preset) => preset.id === id)
}

// 站内渠道类型 -> 对应的官网价源预设。用于「价格摘要 → 更新」时按当前
// 渠道筛选自动预选厂商（23 腾讯云 / 59 华为云，见 constant/channel.go）。
export const CHANNEL_TYPE_TO_VENDOR_PRESET: Record<number, number> = {
  23: TENCENT_VENDOR_PRESET_ID,
  59: HUAWEI_VENDOR_PRESET_ID,
}

// 后端合成的虚拟渠道一律使用负 ID（真实渠道 ID 由自增主键生成，恒为正）。
export function isSynthesizedPresetId(id: number): boolean {
  return id < 0
}

export const OPENROUTER_ENDPOINT = 'openrouter'

// Backend channel type for OpenRouter (see constant/channel.go: ChannelTypeOpenRouter = 20)
export const OPENROUTER_CHANNEL_TYPE = 20

export const ENDPOINT_OPTIONS = [
  { label: 'pricing', value: '/api/pricing' },
  { label: 'ratio_config', value: '/api/ratio_config' },
  { label: 'OpenRouter', value: OPENROUTER_ENDPOINT },
  { label: 'custom', value: 'custom' },
] as const

// Labels reuse the existing sentence-case i18n keys defined for form fields
// (e.g. `Model ratio`, `Audio completion ratio`). Do NOT switch to Title Case
// here without updating the i18n catalog; otherwise we end up with two keys per
// ratio type that only differ in capitalization.
export const RATIO_TYPE_OPTIONS = [
  { label: 'Model ratio', value: 'model_ratio' },
  { label: 'Completion ratio', value: 'completion_ratio' },
  { label: 'Cache ratio', value: 'cache_ratio' },
  { label: 'Create cache ratio', value: 'create_cache_ratio' },
  { label: 'Image ratio', value: 'image_ratio' },
  { label: 'Audio ratio', value: 'audio_ratio' },
  { label: 'Audio completion ratio', value: 'audio_completion_ratio' },
  { label: 'Fixed price', value: 'model_price' },
  { label: 'Expression billing', value: 'billing_expr' },
] as const

export const CHANNEL_STATUS_CONFIG = {
  1: { label: 'Enabled', variant: 'success' as const },
  2: { label: 'Disabled', variant: 'danger' as const },
  3: { label: 'Auto-Disabled', variant: 'warning' as const },
} as const
